(() => {
  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'summary',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  const initPremiumHeader = (header) => {
    if (header.dataset.vvPremiumReady === 'true') return;
    header.dataset.vvPremiumReady = 'true';

    const searchPanel = header.querySelector('#vv-premium-search');
    const searchInput = searchPanel?.querySelector('.search-field');
    const predictiveResults = searchPanel?.querySelector('.thb-predictive-search');
    const searchOpeners = header.querySelectorAll('[data-vv-search-open]');
    const searchCloser = header.querySelector('[data-vv-search-close]');
    const mobileToggle = header.querySelector('.mobile-toggle-wrapper');
    const mobileToggleButton = mobileToggle?.querySelector('.mobile-toggle');
    const mobileDrawer = header.querySelector('#mobile-menu');
    const mobileDrawerInner = mobileDrawer?.querySelector('.vv-premium-drawer__inner');
    const mobilePanels = mobileDrawer?.querySelectorAll('[data-vv-mobile-panel]') || [];
    const mobilePanelOpeners = mobileDrawer?.querySelectorAll('[data-vv-mobile-panel-open]') || [];
    const mobilePanelBacks = mobileDrawer?.querySelectorAll('[data-vv-mobile-panel-back]') || [];
    const megaMenus = header.querySelectorAll('[data-vv-premium-menu]');
    const cartLink = header.querySelector('[data-vv-cart-link]');
    const cartCount = header.querySelector('[data-vv-cart-count]');
    let searchReturnFocus = null;
    let activeMobilePanel = null;
    let mobilePanelReturnFocus = null;
    let mobilePanelEscapeHandled = false;

    const visibleFocusables = (container) => Array.from(container.querySelectorAll(focusableSelector))
      .filter((element) => element.offsetParent !== null && element.getAttribute('aria-hidden') !== 'true');

    const trapFocus = (event, container, leadingElements = []) => {
      if (event.key !== 'Tab') return;
      const focusable = [...leadingElements.filter(Boolean), ...visibleFocusables(container)];
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    const closeMegaMenus = (except = null) => {
      megaMenus.forEach((details) => {
        if (details !== except) details.removeAttribute('open');
      });
    };

    megaMenus.forEach((details) => {
      let hoverCloseTimer;
      details.addEventListener('toggle', () => {
        if (details.open) closeMegaMenus(details);
      });
      details.addEventListener('pointerenter', () => {
        if (!window.matchMedia('(min-width: 1068px) and (hover: hover)').matches) return;
        window.clearTimeout(hoverCloseTimer);
        details.setAttribute('open', '');
      });
      details.addEventListener('pointerleave', () => {
        if (!window.matchMedia('(min-width: 1068px) and (hover: hover)').matches) return;
        hoverCloseTimer = window.setTimeout(() => {
          if (!details.contains(document.activeElement)) details.removeAttribute('open');
        }, 140);
      });
    });

    const closeSearch = ({ restoreFocus = true } = {}) => {
      if (!searchPanel || searchPanel.hidden) return;
      searchPanel.hidden = true;
      searchPanel.setAttribute('aria-hidden', 'true');
      searchOpeners.forEach((button) => button.setAttribute('aria-expanded', 'false'));
      document.body.classList.remove('vv-premium-search-open', 'open-search');
      searchPanel.classList.remove('has-query');
      if (searchInput) searchInput.value = '';
      if (predictiveResults) {
        predictiveResults.setAttribute('inert', '');
        predictiveResults.innerHTML = '';
      }
      if (restoreFocus && searchReturnFocus) searchReturnFocus.focus();
      searchReturnFocus = null;
    };

    const closeMobilePanel = ({ restoreFocus = true } = {}) => {
      if (!activeMobilePanel) return;
      activeMobilePanel.classList.remove('is-active');
      activeMobilePanel.setAttribute('aria-hidden', 'true');
      activeMobilePanel.setAttribute('inert', '');
      mobileDrawerInner?.classList.remove('has-active-panel');
      mobilePanelOpeners.forEach((opener) => opener.setAttribute('aria-expanded', 'false'));
      const returnFocus = mobilePanelReturnFocus;
      activeMobilePanel = null;
      mobilePanelReturnFocus = null;
      if (restoreFocus) window.setTimeout(() => {
        if (mobileToggle?.hasAttribute('open') && returnFocus?.isConnected) returnFocus.focus();
      }, 220);
    };

    const openMobilePanel = (opener) => {
      const panelId = opener.dataset.vvMobilePanelOpen;
      const panel = panelId ? mobileDrawer?.querySelector(`#${CSS.escape(panelId)}`) : null;
      if (!panel) return;
      closeMobilePanel({ restoreFocus: false });
      activeMobilePanel = panel;
      mobilePanelReturnFocus = opener;
      opener.setAttribute('aria-expanded', 'true');
      panel.removeAttribute('inert');
      panel.setAttribute('aria-hidden', 'false');
      mobileDrawerInner?.classList.add('has-active-panel');
      requestAnimationFrame(() => {
        panel.classList.add('is-active');
        panel.querySelector('[data-vv-mobile-panel-back]')?.focus();
      });
    };

    mobilePanelOpeners.forEach((opener) => opener.addEventListener('click', (event) => {
      if (!window.matchMedia('(max-width: 1067px)').matches) return;
      const panelId = opener.dataset.vvMobilePanelOpen;
      if (!panelId || !mobileDrawer?.querySelector(`#${CSS.escape(panelId)}`)) return;
      event.preventDefault();
      openMobilePanel(opener);
    }));
    mobilePanelBacks.forEach((button) => button.addEventListener('click', () => closeMobilePanel()));

    const closeMobile = ({ restoreFocus = true } = {}) => {
      if (!mobileToggle?.hasAttribute('open')) return;
      closeMobilePanel({ restoreFocus: false });
      mobileToggle.classList.remove('active');
      mobileToggle.removeAttribute('open');
      mobileToggleButton?.setAttribute('aria-expanded', 'false');
      mobileToggleButton?.setAttribute('aria-label', 'Abrir menú');
      mobileDrawer?.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('overflow-hidden');
      if (restoreFocus) mobileToggleButton?.focus();
    };

    const openSearch = (trigger) => {
      if (!searchPanel) return;
      closeMegaMenus();
      closeMobile({ restoreFocus: false });
      searchReturnFocus = trigger;
      searchPanel.hidden = false;
      searchPanel.setAttribute('aria-hidden', 'false');
      searchOpeners.forEach((button) => button.setAttribute('aria-expanded', 'true'));
      document.body.classList.add('vv-premium-search-open');
      requestAnimationFrame(() => searchInput?.focus());
    };

    searchOpeners.forEach((button) => button.addEventListener('click', () => openSearch(button)));
    searchCloser?.addEventListener('click', () => closeSearch());
    searchInput?.addEventListener('input', () => {
      searchPanel?.classList.toggle('has-query', searchInput.value.trim().length > 0);
    });

    if (mobileToggle && mobileDrawer && mobileToggleButton) {
      const syncMobileState = () => {
        const isOpen = mobileToggle.hasAttribute('open');
        mobileToggleButton.setAttribute('aria-expanded', String(isOpen));
        mobileToggleButton.setAttribute('aria-label', isOpen ? 'Cerrar menú' : 'Abrir menú');
        mobileDrawer.setAttribute('aria-hidden', String(!isOpen));
        if (isOpen) requestAnimationFrame(() => mobileDrawer.focus());
        else closeMobilePanel({ restoreFocus: false });
      };
      new MutationObserver(syncMobileState).observe(mobileToggle, { attributes: true, attributeFilter: ['open'] });
      syncMobileState();
    }

    mobileDrawer?.addEventListener('click', (event) => {
      if (event.target === mobileDrawer || event.target === mobileDrawerInner) closeMobile();
    });

    if (cartLink && cartCount) {
      const syncCartLabel = () => {
        const count = Number.parseInt(cartCount.textContent.trim(), 10) || 0;
        cartLink.setAttribute('aria-label', `Carrito, ${count} ${count === 1 ? 'artículo' : 'artículos'}`);
      };
      new MutationObserver(syncCartLabel).observe(cartCount, { childList: true, characterData: true, subtree: true });
      syncCartLabel();
    }

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        if (searchPanel && !searchPanel.hidden) {
          event.preventDefault();
          closeSearch();
          return;
        }
        if (mobileToggle?.hasAttribute('open')) {
          event.preventDefault();
          if (activeMobilePanel) {
            mobilePanelEscapeHandled = true;
            closeMobilePanel();
            return;
          }
          closeMobile();
          return;
        }
        closeMegaMenus();
      }

      if (searchPanel && !searchPanel.hidden) trapFocus(event, searchPanel);
      else if (mobileToggle?.hasAttribute('open') && mobileDrawer) trapFocus(event, mobileDrawer, [mobileToggleButton]);
    });

    document.addEventListener('keyup', (event) => {
      if (event.key !== 'Escape' || !mobilePanelEscapeHandled) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      mobilePanelEscapeHandled = false;
    }, true);

    document.addEventListener('click', (event) => {
      if (!header.contains(event.target)) closeMegaMenus();
    });
  };

  const init = () => document.querySelectorAll('[data-vv-premium-header]').forEach(initPremiumHeader);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();

  document.addEventListener('shopify:section:load', init);
})();
